import React from 'react'
import { Modal, ModalBody, Button, Table } from 'reactstrap'
import { X, Search, Info, Package, MapPin, Calendar, Hash, CheckCircle, Database, Copy } from 'react-feather'
import { motion, AnimatePresence } from 'framer-motion'
import { useSnackbar } from 'notistack'
import { P } from 'components'

const ScanDetailModal = ({ isOpen, toggle, data }) => {
  const { enqueueSnackbar } = useSnackbar()
  if (!data) return null

  // Format the display fields
  const fields = [
    { label: 'GTIN', value: data.gtin, icon: <Hash size={16} className="text-muted" /> },
    { label: 'Serial', value: data.serial, icon: <Hash size={16} className="text-muted" /> },
    { label: 'Batch', value: data.batch, icon: <Package size={16} className="text-muted" /> },
    { label: 'Expiry', value: data.expiry, icon: <Calendar size={16} className="text-muted" /> },
    { label: 'Product', value: data.productDescription, icon: <Search size={16} className="text-primary" /> },
    { label: 'RID', value: data.rid, icon: <Info size={16} className="text-muted" /> },
    { label: 'Issuer', value: data.isu, icon: <Info size={16} className="text-muted" /> },
    { label: 'Source', value: data.src, icon: <Info size={16} className="text-muted" /> },
    { label: 'Date', value: data.d, icon: <Calendar size={16} className="text-muted" /> },
    { label: 'Location', value: data.l, icon: <MapPin size={16} className="text-muted" /> },
    { label: 'GLN', value: data.gln, icon: <MapPin size={16} className="text-muted" /> },
    { label: 'Version', value: data.version, icon: <Info size={16} className="text-muted" /> },
    { label: 'Category', value: data.category, icon: <Info size={16} className="text-muted" /> },
    { label: 'QR Type', value: data.qrType, icon: <Info size={16} className="text-muted" /> },
    { label: 'Checksum', value: data.checksum, icon: <CheckCircle size={16} className="text-success" /> },
  ].filter(f => f.value !== undefined && f.value !== null && f.value !== '')

  // PMD handling
  const pmd = data.pmd || null

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      size="lg"
      className="modal-premium"
      contentClassName="border-0 shadow-lg"
      style={{ borderRadius: '16px', overflow: 'hidden' }}
      data-testid="scanner-modal"
    >
      <div className="position-relative">
        <div 
          className="bg-primary p-4 text-white d-flex justify-content-between align-items-center"
          style={{ borderTopLeftRadius: '16px', borderTopRightRadius: '16px' }}
        >
          <div className="d-flex align-items-center gap-2">
            <div className="bg-white bg-opacity-25 p-2 rounded-3">
              <Search size={24} />
            </div>
            <div>
              <h5 className="mb-0 fw-bold">Detail QR PasarMIKRO</h5>
              <small className="opacity-75">Parsed Payload Data</small>
            </div>
          </div>
          <button 
            onClick={toggle} 
            className="btn btn-link text-white p-0 border-0 outline-none shadow-none"
            data-testid="scanner-modal-close"
          >
            <X size={24} />
          </button>
        </div>

        <ModalBody className="p-0">
          <AnimatePresence mode="wait">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="p-4"
            >
              {/* Product Header Card */}
              {data.productDescription && (
                <div className="bg-light p-3 rounded-4 mb-4 border border-primary border-opacity-10 d-flex align-items-center gap-3">
                  <div className="bg-primary bg-opacity-10 p-3 rounded-3 text-primary">
                    <Package size={32} />
                  </div>
                  <div>
                    <P className="mb-0 text-muted small" size="small">Deskripsi Produk</P>
                    <h5 className="mb-0 fw-bold text-primary">{data.productDescription}</h5>
                  </div>
                </div>
              )}

              <div className="row g-3">
                {fields.map((field, idx) => (
                  <div key={idx} className="col-md-6 col-lg-4">
                    <div className="p-3 border rounded-3 h-100 bg-white hover-shadow transition-all">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        {field.icon}
                        <span className="text-muted small fw-medium">{field.label}</span>
                      </div>
                      <div className="fw-bold text-dark text-break" title={field.value} style={{ fontSize: '14px' }}>
                        {field.value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {pmd && Object.keys(pmd).length > 0 && (
                <div className="mt-4 pt-4 border-top">
                  <div className="d-flex align-items-center gap-2 mb-3 text-primary">
                    <Database size={18} />
                    <h6 className="mb-0 fw-bold">Metadata Tambahan (PMD)</h6>
                  </div>
                  <div className="bg-dark bg-opacity-5 p-3 rounded-3 font-monospace small">
                    <pre className="mb-0" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                      {JSON.stringify(pmd, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Raw Value */}
              <div className="mt-4 pt-3 border-top">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <P className="text-muted small mb-0" size="small">Raw Scanned Value</P>
                  <button 
                    className="btn btn-sm btn-link text-primary p-0 d-flex align-items-center gap-1 text-decoration-none"
                    onClick={() => {
                      navigator.clipboard.writeText(data.value)
                      enqueueSnackbar('Raw value disalin ke clipboard', { variant: 'info', autoHideDuration: 2000 })
                    }}
                  >
                    <Copy size={12} />
                    <span style={{ fontSize: '11px' }}>Salin</span>
                  </button>
                </div>
                <div 
                  className="bg-light p-3 rounded text-break font-monospace position-relative border" 
                  style={{ 
                    fontSize: '12px', 
                    color: '#333', 
                    minHeight: '80px',
                    maxHeight: '200px', 
                    overflowY: 'auto',
                    lineHeight: '1.5',
                    backgroundColor: '#f8f9fa'
                  }}
                >
                  {data.value}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </ModalBody>

        <div className="p-4 bg-light border-top d-flex justify-content-end gap-2" style={{ borderBottomLeftRadius: '16px', borderBottomRightRadius: '16px' }}>
          <Button
            color="primary"
            className="px-4 fw-bold rounded-3"
            onClick={toggle}
            data-testid="scanner-modal-action"
          >
            Selesai
          </Button>
        </div>
      </div>
      
      <style>{`
        .modal-premium .modal-content {
          border-radius: 16px;
        }
        .hover-shadow:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          border-color: var(--bs-primary) !important;
        }
        .transition-all {
          transition: all 0.2s ease-in-out;
        }
      `}</style>
    </Modal>
  )
}

export default ScanDetailModal
